'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { formatNPR } from '@/lib/i18n';
import LocationBrowser, { LocationSelection, formatLocationSelection } from '@/components/LocationBrowser';
import InteractiveMap, { MapMarker } from '@/components/InteractiveMap';

interface Vehicle {
  id: string;
  model: string;
  vehicle_type: string;
  seat_capacity: number;
  price_per_day: number;
  rating: number;
  reviews_count: number;
  is_available: boolean;
  is_featured: boolean;
  owner_verified: boolean;
  image_url?: string;
  features?: string[];
  location?: string;
  description?: string;
}

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
  const [locationSel, setLocationSel] = useState<LocationSelection>({});
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      const supabase = createClient();
      const { data } = await supabase.from('vehicles').select('*').order('is_featured', { ascending: false }).order('rating', { ascending: false });
      if (data) {
        setVehicles(data);
        const uniqueTypes = [...new Set(data.map((v: Vehicle) => v.vehicle_type).filter(Boolean))];
        setVehicleTypes(uniqueTypes);
      }
      setLoading(false);
    };
    fetchVehicles();
  }, []);

  const locationLabel = formatLocationSelection(locationSel);

  const filtered = vehicles.filter(v => {
    const matchSearch = !search || v.model?.toLowerCase().includes(search.toLowerCase()) || v.vehicle_type?.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || v.vehicle_type === typeFilter;
    const matchAvail = !availableOnly || v.is_available;
    const matchLocation = !locationSel.province && !locationSel.district && !locationSel.city
      ? true
      : (locationSel.city ? v.location?.toLowerCase().includes(locationSel.city.name.toLowerCase().split(' ')[0]) :
         locationSel.district ? v.location?.toLowerCase().includes(locationSel.district.name.toLowerCase()) :
         locationSel.province ? v.location?.toLowerCase().includes(locationSel.province.name.toLowerCase()) : true);
    return matchSearch && matchType && matchAvail && matchLocation;
  });

  const vehicleCoords: Record<string, { lat: number; lng: number }> = {
    'kathmandu': { lat: 27.7172, lng: 85.3240 },
    'pokhara': { lat: 28.2096, lng: 83.9856 },
    'chitwan': { lat: 27.5291, lng: 84.3542 },
    'biratnagar': { lat: 26.4525, lng: 87.2718 },
    'butwal': { lat: 27.7006, lng: 83.4483 },
  };

  const mapMarkers: MapMarker[] = filtered.map(v => {
    const locKey = Object.keys(vehicleCoords).find(k => v.location?.toLowerCase().includes(k));
    const coords = locKey ? vehicleCoords[locKey] : { lat: 27.7172 + (Math.random() - 0.5) * 0.3, lng: 85.3240 + (Math.random() - 0.5) * 0.3 };
    return {
      id: v.id,
      lat: coords.lat,
      lng: coords.lng,
      title: v.model,
      description: `${v.vehicle_type} · ${v.seat_capacity} seats · ${v.is_available ? '✅ Available' : '❌ Unavailable'}`,
      category: 'vehicle',
      price: v.price_per_day ? `NPR ${v.price_per_day.toLocaleString()}/day` : undefined,
      rating: v.rating,
      image: v.image_url,
      link: v.is_available ? `/vehicles/${v.id}` : undefined,
    };
  });

  return (
    <main className="min-h-screen bg-navy">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 mb-8">
          <div className="text-center mb-8">
            <h1 className="text-snow font-bold text-3xl md:text-4xl mb-3">Vehicle Rentals in Nepal</h1>
            <p className="text-snow/60 text-lg max-w-2xl mx-auto">Jeeps, buses, motorcycles and more — explore Nepal on your own terms.</p>
          </div>

          {/* Location Browser */}
          <div className="yn-card rounded-2xl p-4 mb-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-gold text-sm">📍</span>
              <h3 className="text-snow text-sm font-semibold">Filter by Location</h3>
              {locationLabel && (
                <span className="ml-auto text-xs text-gold/80 bg-gold/10 px-2 py-0.5 rounded-full truncate max-w-xs">{locationLabel}</span>
              )}
              {locationLabel && (
                <button onClick={() => setLocationSel({})} className="text-snow/40 hover:text-snow/70 text-xs transition-colors ml-1">✕ Clear</button>
              )}
            </div>
            <LocationBrowser
              nepalOnly
              levels={['province', 'district', 'city']}
              value={locationSel}
              onChange={setLocationSel}
              compact
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl mx-auto">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vehicles..."
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow focus:outline-none focus:border-gold/40">
              <option value="all">All Types</option>
              {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:border-gold/30 transition-colors">
              <input type="checkbox" checked={availableOnly} onChange={e => setAvailableOnly(e.target.checked)} className="accent-gold" />
              <span className="text-snow text-sm">Available only</span>
            </label>
            <button
              onClick={() => setShowMap(m => !m)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-medium transition-all whitespace-nowrap ${showMap ? 'bg-gold/20 border-gold/50 text-gold' : 'bg-white/5 border-white/10 text-snow/70 hover:text-snow hover:border-gold/30'}`}
            >
              🗺️ {showMap ? 'Hide Map' : 'Map View'}
            </button>
          </div>
        </div>

        {/* Interactive Map */}
        {showMap && (
          <div className="max-w-7xl mx-auto px-4 mb-8">
            <div className="yn-card rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-gold text-lg">🗺️</span>
                <h2 className="text-snow font-bold text-lg">Vehicles Map</h2>
                <span className="text-snow/40 text-xs ml-auto">Satellite · Traffic · Route Planning · Nearby Search</span>
              </div>
              <InteractiveMap
                markers={mapMarkers}
                centerLat={27.7172}
                centerLng={85.3240}
                zoom={8}
                height="460px"
                showControls
                showNearbySearch
                showRoutePlanning
              />
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-64 bg-white/5 rounded-2xl animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">🚗</div>
              <h3 className="text-snow font-semibold mb-2">No vehicles found</h3>
              <p className="text-snow/40 text-sm">Try adjusting your search or location filters</p>
              <button onClick={() => { setSearch(''); setTypeFilter('all'); setAvailableOnly(false); setLocationSel({}); }}
                className="mt-4 text-gold text-sm hover:underline">Clear all filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(vehicle => (
                <div key={vehicle.id} className="yn-card rounded-2xl overflow-hidden hover:border-gold/30 transition-all group">
                  <div className="h-44 bg-gradient-to-br from-navy-mid to-navy relative overflow-hidden">
                    {vehicle.image_url ? (
                      <img src={vehicle.image_url} alt={`${vehicle.model} vehicle rental in Nepal`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><span className="text-5xl opacity-30">🚗</span></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      {vehicle.is_featured && <span className="bg-gold text-navy text-[10px] font-bold px-2 py-0.5 rounded-full">⭐ Featured</span>}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${vehicle.is_available ? 'bg-green-400 text-navy' : 'bg-red-400/80 text-white'}`}>
                        {vehicle.is_available ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                    {vehicle.owner_verified && (
                      <div className="absolute top-3 right-3 bg-sky/20 text-sky text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky/30">✓ Verified</div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-snow font-bold text-base">{vehicle.model}</h3>
                      <span className="text-snow/50 text-xs bg-white/5 px-2 py-0.5 rounded-full">{vehicle.vehicle_type}</span>
                    </div>
                    {vehicle.location && <p className="text-snow/50 text-xs mb-2">📍 {vehicle.location}</p>}
                    {vehicle.description && <p className="text-snow/60 text-xs line-clamp-2 mb-3">{vehicle.description}</p>}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3 text-xs text-snow/50">
                        <span>👥 {vehicle.seat_capacity} seats</span>
                        <span>⭐ {vehicle.rating || '—'} ({vehicle.reviews_count || 0})</span>
                      </div>
                      <div className="text-right">
                        <div className="text-gold font-bold text-sm">{formatNPR(vehicle.price_per_day)}</div>
                        <div className="text-snow/40 text-[10px]">per day</div>
                      </div>
                    </div>
                    {vehicle.features && vehicle.features.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {vehicle.features.slice(0, 3).map((f: string) => (
                          <span key={f} className="text-[10px] px-2 py-0.5 bg-white/5 text-snow/50 rounded-full">{f}</span>
                        ))}
                      </div>
                    )}
                    <Link href={vehicle.is_available ? `/vehicles/${vehicle.id}` : '#'}
                      className={`block w-full text-center py-2 rounded-xl text-sm font-medium transition-colors ${vehicle.is_available ? 'bg-gold/20 text-gold border border-gold/30 hover:bg-gold/30' : 'bg-white/5 text-snow/30 border border-white/10 cursor-not-allowed'}`}>
                      {vehicle.is_available ? 'Book Now' : 'Not Available'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
